/* eslint-disable @typescript-eslint/no-explicit-any */
import queryClient from '@api/datasource/query'
import { UseQueryResult } from '@tanstack/react-query'

export const isLoadingQuery = (...results: UseQueryResult[]) => {
  return results.some((r) => r.isLoading || r.isPending)
}

export const isLoadingOrRefetchQuery = (...results: UseQueryResult[]) => {
  return results.some((r) => r.isRefetching || r.isLoading)
}

export const isRefetchingQuery = (...results: UseQueryResult[]) => {
  return results.some((r) => r.isRefetching)
}

export const isLoadingMutation = (...results: any[]) => {
  return results.some((r) => r.isPending && !r.isIdle)
}

type InvalidateDataQueryParams<T> = {
  queryKeys: any[]
  exact?: boolean
  data?: T
}

export const invalidateDataQuery = async <T>(params: InvalidateDataQueryParams<T>) => {
  const { queryKeys, exact, data } = params
  if (exact) {
    if (data) {
      queryClient.setQueryData<T, string[], T>(queryKeys, (prev) => ({
        ...prev,
        ...data,
      }))
    } else {
      queryClient.setQueryData(queryKeys, undefined)
    }

    return await queryClient.invalidateQueries({
      queryKey: queryKeys,
      refetchType: 'all',
      exact: true,
    })
  }

  return await queryClient.invalidateQueries({
    predicate: (query) => queryKeys.includes(query.queryKey[0] as string),
    refetchType: 'all',
  })
}

const updateTimeouts = new Map<string, NodeJS.Timeout>()

type DebounceUpdateParams<TData> = {
  callId: string
  dataFunction: TData
  updateFn: (data: TData) => void
  delay?: number
}

export const debounceUpdate = <TData>(params: DebounceUpdateParams<TData>) => {
  const { callId, dataFunction, delay = 500, updateFn } = params

  if (updateTimeouts.has(callId)) {
    clearTimeout(updateTimeouts.get(callId))
  }

  const timeout = setTimeout(() => {
    updateFn(dataFunction)
    updateTimeouts.delete(callId)
  }, delay)

  updateTimeouts.set(callId, timeout)
}
